<template>
  <b-modal
    v-model="show"
    :title="form.id ? 'Edit Notice' : 'New Notice'"
    size="lg"
    ok-title="Save"
    :ok-disabled="saving"
    no-close-on-backdrop
    @ok="handleSave"
  >
    <b-overlay :show="saving">
      <b-form @submit.prevent>
        <b-alert v-if="form.isSystem" show variant="secondary" class="p-1">
          This is the Client Declaration shown on the Review &amp; sign step of every form.
          Only its title, body and acknowledgement label can be changed.
        </b-alert>

        <b-form-group label="Title">
          <b-form-input v-model="form.title" placeholder="e.g. Risk warning" autofocus />
          <small v-if="form.key" class="text-muted">Key <code>{{ form.key }}</code></small>
        </b-form-group>

        <b-form-group>
          <template #label>
            <div class="d-flex justify-content-between align-items-center">
              <span>Body</span>
              <b-button size="sm" variant="flat-secondary" @click="previewBody = !previewBody">
                <feather-icon :icon="previewBody ? 'EditIcon' : 'EyeIcon'" class="mr-25" />
                {{ previewBody ? 'Edit' : 'Preview' }}
              </b-button>
            </div>
          </template>
          <div v-if="previewBody" class="notice-body-preview">{{ form.body || 'Nothing to preview yet.' }}</div>
          <b-form-textarea
            v-else
            v-model="form.body"
            rows="12"
            max-rows="40"
            class="notice-body-editor"
            placeholder="The text the client must read. Line breaks are kept."
          />
          <small class="text-muted">{{ bodyLength }} characters. Shown to the client exactly as typed, line breaks included.</small>
        </b-form-group>

        <b-row>
          <b-col cols="12" md="6">
            <b-form-group label="Acknowledgement">
              <b-form-radio-group v-model="form.ackMode" stacked :disabled="form.isSystem">
                <b-form-radio value="Tick">
                  Tick box
                  <small class="text-muted d-block">The client ticks a box to confirm.</small>
                </b-form-radio>
                <b-form-radio value="Signature">
                  Name + drawn signature
                  <small class="text-muted d-block">The client types a name and signs on screen.</small>
                </b-form-radio>
              </b-form-radio-group>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group label="Acknowledgement label">
              <b-form-input v-model="form.ackLabel" :placeholder="ackPlaceholder" />
              <small class="text-muted">Optional, replaces the default wording next to the tick box or signature.</small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group v-if="!form.isSystem" label="Default section">
          <v-select
            v-model="form.defaultSectionKey"
            :options="accountSections"
            label="Title"
            :reduce="s => s.Key"
            placeholder="Pick a section"
          >
            <template #option="s">
              <code class="mr-50">{{ s.Key }}</code>{{ s.Title }}
            </template>
          </v-select>
          <small class="text-muted">Where the notice is placed when added to a package.</small>
        </b-form-group>

        <template v-if="form.id">
          <hr />
          <b-form-group>
            <b-form-checkbox v-model="form.applyToExisting">
              Apply this change to existing onboardings
            </b-form-checkbox>
            <small class="text-muted">
              Updates the notice on the {{ plural(existingOnboardingCount, 'onboarding') }} already under way,
              including forms clients have already signed.
            </small>
          </b-form-group>
          <b-form-group v-if="form.applyToExisting">
            <b-form-checkbox v-model="form.notify">
              Email affected clients
            </b-form-checkbox>
          </b-form-group>
        </template>
      </b-form>
    </b-overlay>
  </b-modal>
</template>

<script>
import {
  BModal, BOverlay, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox,
  BFormRadio, BFormRadioGroup, BRow, BCol, BAlert, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import { apiError, plural } from './builderHelpers'

function emptyForm() {
  return {
    id: null,
    key: '',
    title: '',
    body: '',
    ackMode: 'Tick',
    ackLabel: '',
    defaultSectionKey: null,
    isSystem: false,
    applyToExisting: false,
    notify: false,
  }
}

export default {
  name: 'NoticeModal',

  components: {
    BModal, BOverlay, BForm, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox,
    BFormRadio, BFormRadioGroup, BRow, BCol, BAlert, BButton, vSelect,
  },

  props: {
    data: { type: Object, required: true },
  },

  data() {
    return {
      show: false,
      saving: false,
      previewBody: false,
      form: emptyForm(),
    }
  },

  computed: {
    accountSections() {
      return (this.data.sections || []).filter(s => !s.PersonType)
    },
    bodyLength() {
      return (this.form.body || '').length
    },
    ackPlaceholder() {
      return this.form.ackMode === 'Signature'
        ? 'Name (only by an authorised signatory)'
        : 'I have read and understood…'
    },
    existingOnboardingCount() {
      return this.data.existingOnboardingCount || 0
    },
  },

  methods: {
    plural,

    // open(null, { defaultSectionKey }) for a new notice, open(notice) to edit.
    open(notice, presets) {
      const form = emptyForm()
      if (notice) {
        form.id = notice.id
        form.key = notice.Key || ''
        form.title = notice.Title || ''
        form.body = notice.Body || ''
        form.ackMode = notice.AckMode || 'Tick'
        form.ackLabel = notice.AckLabel || ''
        form.defaultSectionKey = notice.DefaultSectionKey || null
        form.isSystem = !!notice.IsSystem
      } else if (presets && presets.defaultSectionKey) {
        form.defaultSectionKey = presets.defaultSectionKey
      }
      this.form = form
      this.previewBody = false
      this.show = true
    },

    handleSave(evt) {
      evt.preventDefault()
      const f = this.form
      if (!f.title.trim()) {
        this.$bvToast.toast('Title is required.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      if (!f.body.trim()) {
        this.$bvToast.toast('Body is required.', { variant: 'danger', title: 'Validation', solid: true })
        return
      }
      const payload = {
        id: f.id || undefined,
        title: f.title.trim(),
        body: f.body,
        ackMode: f.ackMode || 'Tick',
        ackLabel: f.ackLabel.trim() || null,
        defaultSectionKey: f.defaultSectionKey || null,
        applyToExisting: !!f.id && !!f.applyToExisting,
        notify: !!f.id && !!f.applyToExisting && !!f.notify,
      }
      this.saving = true
      store.dispatch('settings/saveOnboardingNotice', payload)
        .then(res => {
          this.$bvToast.toast(res.data.message || 'Notice saved.', { variant: 'success', title: 'Success', solid: true })
          this.show = false
          this.$emit('saved', res.data.notice || null)
        })
        .catch(err => {
          this.$bvToast.toast(apiError(err), { variant: 'danger', title: 'Error', solid: true })
        })
        .finally(() => { this.saving = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.notice-body-editor {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.85rem;
}
.notice-body-preview {
  white-space: pre-wrap;
  min-height: 12rem;
  max-height: 60vh;
  overflow: auto;
  padding: 0.75rem 1rem;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  background: #f8f8f8;
  font-size: 0.95rem;
  line-height: 1.5;
}
</style>
